/**
 * WornVaultSection — Admin-Only Zugriff auf den Worn-Originals-Vault (iter88.21).
 *
 * Neu ggü. iter88.20:
 *   • TOTP-Setup-Wizard direkt im Gate: wenn `totp_enabled=false` → QR + Verify → aktivieren
 *   • Alternative Notification-Email: nach erfolgreicher 2FA änderbar (PUT /notify-email)
 *   • Ganzer Component läuft als Sub-Tab in `MediaAdminSection` (kein eigener Sidebar-Eintrag mehr)
 */
import { useState, useEffect, useCallback } from "react";
import api, { formatError } from "@/lib/api";
import { Lock, ShieldCheck, KeyRound, AlertTriangle, Trash2, RefreshCw,
         Eye, X, QrCode, Mail, Pencil, CheckCircle2 } from "lucide-react";

const VAULT_TOKEN_KEY = "worn_vault_session_token";
const VAULT_TOKEN_EXPIRES_KEY = "worn_vault_session_expires";

function loadToken() {
  const t = localStorage.getItem(VAULT_TOKEN_KEY);
  const exp = localStorage.getItem(VAULT_TOKEN_EXPIRES_KEY);
  if (!t || !exp) return null;
  if (new Date(exp).getTime() <= Date.now()) {
    localStorage.removeItem(VAULT_TOKEN_KEY);
    localStorage.removeItem(VAULT_TOKEN_EXPIRES_KEY);
    return null;
  }
  return { token: t, expires: exp };
}

function saveToken(token, expires) {
  localStorage.setItem(VAULT_TOKEN_KEY, token);
  localStorage.setItem(VAULT_TOKEN_EXPIRES_KEY, expires);
}

function clearToken() {
  localStorage.removeItem(VAULT_TOKEN_KEY);
  localStorage.removeItem(VAULT_TOKEN_EXPIRES_KEY);
}

export default function WornVaultSection() {
  const [session, setSession] = useState(loadToken());
  // gate | totp_setup | challenge | ready
  const [phase, setPhase] = useState(session ? "ready" : "gate");
  const [gateStatus, setGateStatus] = useState(null);
  const [method, setMethod] = useState("TOTP");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const [entries, setEntries] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [preview, setPreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");

  // TOTP setup state
  const [totpSetup, setTotpSetup] = useState(null); // {qr_data_uri, secret}
  const [totpRecovery, setTotpRecovery] = useState(null);

  // Notify-Email state (only editable inside `ready` phase)
  const [notifyEmail, setNotifyEmail] = useState("");
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");

  const vaultHeaders = useCallback(() =>
    session ? { "X-Vault-Token": session.token } : {}, [session]);

  const loadGateStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/worn-vault/gate-status");
      setGateStatus(r.data);
      setNotifyEmail(r.data?.notify_email || "");
    } catch (e) { setErr(formatError(e)); }
  }, []);

  useEffect(() => {
    if (phase === "gate") loadGateStatus();
  }, [phase, loadGateStatus]);

  const requestChallenge = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      // TOTP-Setup-Zwischenschritt, falls noch nicht aktiviert
      if (method === "TOTP" && gateStatus && !gateStatus.totp_enabled) {
        const r = await api.post("/admin/worn-vault/totp-setup-init");
        setTotpSetup(r.data);
        setPhase("totp_setup");
        setInfo("Scanne den QR-Code mit deiner Authenticator-App und gib dann den 6-stelligen Code ein.");
        return;
      }
      const r = await api.post("/admin/worn-vault/challenge", { method });
      setInfo(r.data?.prompt || "Bitte gib deinen Code ein.");
      setPhase("challenge");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const submitTotpSetup = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const r = await api.post("/admin/worn-vault/totp-setup-verify",
                                { method: "TOTP", code: code.trim() });
      setTotpRecovery(r.data.recovery_codes || []);
      setCode(""); setTotpSetup(null);
      // Direkt weiter in den Challenge-Schritt
      const ch = await api.post("/admin/worn-vault/challenge", { method: "TOTP" });
      setInfo(ch.data?.prompt || "Öffne die Authenticator-App und gib den 6-stelligen Code ein.");
      setPhase("challenge");
      await loadGateStatus();
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const submitCode = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const r = await api.post("/admin/worn-vault/verify", { method, code: code.trim() });
      const { vault_session_token, expires_at } = r.data;
      saveToken(vault_session_token, expires_at);
      setSession({ token: vault_session_token, expires: expires_at });
      setCode("");
      setPhase("ready");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const loadEntries = useCallback(async () => {
    if (!session) return;
    setBusy(true); setErr("");
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status", statusFilter);
      if (searchQ.trim()) qs.set("q", searchQ.trim());
      const params = qs.toString() ? `?${qs.toString()}` : "";
      const r = await api.get(`/admin/worn-vault/entries${params}`, { headers: vaultHeaders() });
      setEntries(r.data?.entries || []);
    } catch (e) {
      const msg = formatError(e);
      if (msg.includes("VAULT_SESSION")) {
        clearToken(); setSession(null); setPhase("gate");
      } else { setErr(msg); }
    } finally { setBusy(false); }
  }, [session, statusFilter, searchQ, vaultHeaders]);

  useEffect(() => {
    if (phase === "ready") {
      loadEntries();
      loadGateStatus();
    }
  }, [phase, statusFilter, loadEntries, loadGateStatus]);

  const showOriginal = async (vault_id) => {
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/admin/worn-vault/entries/${vault_id}/original`, {
        headers: vaultHeaders(), responseType: "blob",
      });
      const url = URL.createObjectURL(r.data);
      setPreview({ vault_id, url, mime: r.data.type });
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  const openDetail = async (vault_id) => {
    setBusy(true); setErr("");
    try {
      const r = await api.get(`/admin/worn-vault/entries/${vault_id}`, { headers: vaultHeaders() });
      setDetail(r.data);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const doDelete = async (vault_id) => {
    if (!window.confirm("Original + alle bearbeiteten Ableger werden sofort gelöscht. Fortfahren?")) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/worn-vault/entries/${vault_id}/delete`,
                     { reason: deleteReason || "ADMIN_MANUAL" },
                     { headers: vaultHeaders() });
      setDeletingId(null); setDeleteReason("");
      await loadEntries();
      setInfo("Eintrag gelöscht.");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const runCleanup = async () => {
    if (!window.confirm("Alle fälligen Löschungen jetzt ausführen?")) return;
    setBusy(true); setErr("");
    try {
      const r = await api.post("/admin/worn-vault/run-cleanup", {}, { headers: vaultHeaders() });
      setInfo(`Cleanup: ${r.data.deleted_scheduled} geplant, ${r.data.deleted_orphans} Waisenkinder gelöscht.`);
      await loadEntries();
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const saveNotifyEmail = async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      const r = await api.put("/admin/worn-vault/notify-email",
                               { email: emailDraft.trim() },
                               { headers: vaultHeaders() });
      setNotifyEmail(r.data.notify_email);
      setEditingEmail(false);
      setInfo("E-Mail-Adresse für Vault-Codes aktualisiert.");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  const logout = () => {
    clearToken(); setSession(null); setPhase("gate"); setEntries([]);
    setTotpRecovery(null);
  };

  // ─── Render ─────────────────────────────────────────────────

  if (phase === "gate") {
    return (
      <div data-testid="worn-vault-section" className="max-w-lg">
        <GateHeader />
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <Lock className="w-8 h-8 text-[#d97a63]" />
            <div>
              <h2 className="text-lg font-bold">Zugriff geschützt</h2>
              <p className="text-sm text-[#666]">Wähle deine 2-Faktor-Methode, um den Vault zu öffnen.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["TOTP", "EMAIL_OTP"].map((m) => (
              <button key={m} data-testid={`worn-vault-method-${m.toLowerCase()}`}
                onClick={() => setMethod(m)}
                className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${method === m ? "border-[#d97a63] bg-[#fdf2ee] text-[#0A0A0A]" : "border-[#E5E5E5] text-[#666]"}`}>
                {m === "TOTP" ? "Authenticator (TOTP)" : "E-Mail-Code"}
              </button>
            ))}
          </div>
          {method === "TOTP" && gateStatus && !gateStatus.totp_enabled && (
            <div data-testid="worn-vault-totp-not-enabled" className="text-xs px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
              <AlertTriangle size={13} className="inline mr-1" />
              TOTP ist noch nicht aktiviert. Nach „Code anfordern" erhältst du den QR-Code zum Scannen.
            </div>
          )}
          {method === "EMAIL_OTP" && gateStatus && (
            <div data-testid="worn-vault-email-info" className="text-xs px-3 py-2 bg-[#F5F5F5] border border-[#E5E5E5] rounded-lg text-[#666]">
              <Mail size={13} className="inline mr-1" />
              Code geht an: <span className="font-mono text-[#0A0A0A]">{notifyEmail || gateStatus.login_email}</span>
              <br /><span className="text-[10px] text-[#999]">Adresse ist nach dem Einloggen im Vault änderbar.</span>
            </div>
          )}
          {err && <div data-testid="worn-vault-error" className="text-sm text-red-600">{err}</div>}
          <button data-testid="worn-vault-request-challenge" onClick={requestChallenge} disabled={busy}
            className="w-full h-11 rounded-lg bg-[#0A0A0A] text-white text-sm font-medium hover:bg-[#333] disabled:opacity-50">
            {busy ? "Bitte warten…" : (method === "TOTP" && gateStatus && !gateStatus.totp_enabled ? "TOTP jetzt einrichten" : "Code anfordern")}
          </button>
        </div>
      </div>
    );
  }

  if (phase === "totp_setup") {
    return (
      <div data-testid="worn-vault-section" className="max-w-lg">
        <GateHeader />
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <QrCode className="w-8 h-8 text-[#d97a63]" />
            <div>
              <h2 className="text-lg font-bold">Authenticator einrichten</h2>
              {info && <p data-testid="worn-vault-prompt" className="text-sm text-[#666]">{info}</p>}
            </div>
          </div>
          {totpSetup?.qr_data_uri && (
            <div className="flex justify-center">
              <img data-testid="worn-vault-qr" src={totpSetup.qr_data_uri} alt="TOTP QR-Code" className="w-56 h-56" />
            </div>
          )}
          <div className="text-xs text-center">
            <div className="text-[#666]">Kein QR-Scan möglich? Secret manuell eingeben:</div>
            <code data-testid="worn-vault-totp-secret" className="inline-block mt-1 px-2 py-1 bg-[#F5F5F5] rounded font-mono text-[#0A0A0A]">
              {totpSetup?.secret}
            </code>
          </div>
          <div>
            <label className="text-xs text-[#666]">6-stelliger Code aus App</label>
            <input data-testid="worn-vault-code-input" value={code}
              onChange={(e) => setCode(e.target.value)} autoFocus
              maxLength={12} inputMode="numeric"
              placeholder="123 456"
              className="w-full h-12 px-4 rounded-lg border border-[#E5E5E5] text-lg tracking-widest text-center focus:border-[#d97a63] focus:outline-none" />
          </div>
          {err && <div data-testid="worn-vault-error" className="text-sm text-red-600">{err}</div>}
          <div className="flex gap-2">
            <button data-testid="worn-vault-totp-activate" onClick={submitTotpSetup}
              disabled={busy || code.length < 6}
              className="flex-1 h-11 rounded-lg bg-[#d97a63] text-white text-sm font-medium hover:bg-[#c76b56] disabled:opacity-50">
              {busy ? "Aktiviere…" : "Authenticator aktivieren"}
            </button>
            <button onClick={() => { setPhase("gate"); setTotpSetup(null); setCode(""); setErr(""); }}
              className="px-4 h-11 rounded-lg border border-[#E5E5E5] text-sm">Zurück</button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "challenge") {
    return (
      <div data-testid="worn-vault-section" className="max-w-lg">
        <GateHeader />
        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6 space-y-4">
          <div className="flex items-center gap-3">
            <KeyRound className="w-8 h-8 text-[#d97a63]" />
            <div>
              <h2 className="text-lg font-bold">Code eingeben</h2>
              {info && <p data-testid="worn-vault-prompt" className="text-sm text-[#666]">{info}</p>}
            </div>
          </div>
          {totpRecovery && (
            <div data-testid="worn-vault-recovery" className="text-xs px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="font-medium text-emerald-800 mb-1">Recovery-Codes (bitte sicher aufbewahren!):</p>
              <code className="block whitespace-pre-wrap text-emerald-900">{totpRecovery.join("  ")}</code>
            </div>
          )}
          <input data-testid="worn-vault-code-input" value={code}
            onChange={(e) => setCode(e.target.value)} autoFocus
            maxLength={12} inputMode="numeric"
            placeholder="6-stelliger Code"
            className="w-full h-12 px-4 rounded-lg border border-[#E5E5E5] text-lg tracking-widest text-center focus:border-[#d97a63] focus:outline-none" />
          {err && <div data-testid="worn-vault-error" className="text-sm text-red-600">{err}</div>}
          <div className="flex gap-2">
            <button data-testid="worn-vault-verify" onClick={submitCode} disabled={busy || code.length < 4}
              className="flex-1 h-11 rounded-lg bg-[#d97a63] text-white text-sm font-medium hover:bg-[#c76b56] disabled:opacity-50">
              {busy ? "Prüfe…" : "Vault öffnen"}
            </button>
            <button onClick={() => { setPhase("gate"); setCode(""); setErr(""); }}
              className="px-4 h-11 rounded-lg border border-[#E5E5E5] text-sm">Zurück</button>
          </div>
        </div>
      </div>
    );
  }

  // ─── phase === "ready" ──────────────────────────────

  return (
    <div data-testid="worn-vault-section" className="space-y-4">
      <ReadyHeader session={session} onLogout={logout} onCleanup={runCleanup} />

      {/* Notification-Email verwalten (nur post-2FA sichtbar) */}
      <div className="bg-white border border-[#E5E5E5] rounded-xl p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm">
            <Mail size={14} className="text-[#666]" />
            <span className="text-[#666]">E-Mail für Vault-Codes:</span>
            {!editingEmail ? (
              <span data-testid="worn-vault-current-notify-email" className="font-mono text-[#0A0A0A]">{notifyEmail || gateStatus?.login_email}</span>
            ) : (
              <input data-testid="worn-vault-notify-email-input" type="email" value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                className="px-3 py-1.5 rounded border border-[#E5E5E5] text-sm min-w-[240px]"
                placeholder="alternative@mail.de" />
            )}
          </div>
          {!editingEmail ? (
            <button data-testid="worn-vault-edit-email" onClick={() => { setEmailDraft(notifyEmail || gateStatus?.login_email || ""); setEditingEmail(true); }}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#d97a63] hover:underline">
              <Pencil size={12} /> Ändern
            </button>
          ) : (
            <div className="flex gap-1">
              <button data-testid="worn-vault-save-email" onClick={saveNotifyEmail} disabled={busy}
                className="px-3 py-1.5 rounded bg-[#d97a63] text-white text-xs">Speichern</button>
              <button onClick={() => setEditingEmail(false)}
                className="px-3 py-1.5 rounded border border-[#E5E5E5] text-xs">Abbrechen</button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 text-sm">
        <label className="text-[#666]">Status:</label>
        <select data-testid="worn-vault-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 rounded-lg border border-[#E5E5E5] text-sm">
          <option value="">Aktiv (Default)</option>
          <option value="ACTIVE">Aktiv</option>
          <option value="SCHEDULED_DELETE">Zum Löschen geplant</option>
          <option value="DELETED">Bereits gelöscht</option>
        </select>
        <button data-testid="worn-vault-refresh" onClick={loadEntries}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#E5E5E5] text-sm hover:bg-[#F5F5F5]">
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {info && <div data-testid="worn-vault-info" className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 inline-flex items-center gap-2"><CheckCircle2 size={14} />{info}</div>}
      {err && <div data-testid="worn-vault-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

      {/* iter172l · Suchfeld für Seller-Name / Handle / Listing-ID */}
      <div className="flex items-center gap-2">
        <input
          data-testid="worn-vault-search"
          type="text"
          placeholder="Suche nach Verkäufer, Handle oder Listing-ID …"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") loadEntries(); }}
          className="flex-1 h-9 px-3 rounded border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
        />
        <button
          data-testid="worn-vault-search-btn"
          onClick={loadEntries}
          className="h-9 px-3 rounded border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]"
        >
          Suchen
        </button>
        {searchQ && (
          <button
            data-testid="worn-vault-search-clear"
            onClick={() => { setSearchQ(""); setTimeout(loadEntries, 50); }}
            className="h-9 px-3 rounded border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]"
          >
            Löschen
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="worn-vault-table">
          <thead className="bg-[#FAFAFA] text-left text-xs uppercase text-[#666]">
            <tr>
              <th className="px-3 py-2">Vorschau</th>
              <th className="px-3 py-2">Verkäufer</th>
              <th className="px-3 py-2">Listing</th>
              <th className="px-3 py-2">Erstellt</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Ableger</th>
              <th className="px-3 py-2 text-right">Aktion</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-[#888]">Keine Einträge.</td></tr>
            )}
            {entries.map((e) => (
              <tr key={e.vault_id} data-testid={`worn-vault-row-${e.vault_id}`} className="border-t border-[#F0F0F0] hover:bg-[#FAFAFA]">
                <td className="px-3 py-2">
                  {/* iter172l · Vorschaubild (anonymisierte Version, admin-safe) */}
                  {e.preview_url ? (
                    <img
                      data-testid={`worn-vault-thumb-${e.vault_id}`}
                      src={e.preview_url}
                      alt="Vorschau"
                      className="w-12 h-16 object-cover rounded border border-[#E5E5E5] bg-[#F5F5F5]"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-12 h-16 rounded border border-[#E5E5E5] bg-[#F5F5F5] flex items-center justify-center text-[9px] text-[#999]">—</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  {/* iter172l · Uploader-Info: Avatar + Name + Handle */}
                  <div className="flex items-center gap-2">
                    {e.seller_avatar_url ? (
                      <img src={e.seller_avatar_url} alt="" className="w-7 h-7 rounded-full object-cover border border-[#E5E5E5]" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#F0F0F0] flex items-center justify-center text-[10px] text-[#999]">?</div>
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-[#0A0A0A] truncate" data-testid={`worn-vault-seller-${e.vault_id}`}>{e.seller_display_name || "—"}</p>
                      {e.seller_handle && <p className="text-[10px] text-[#999] truncate">@{e.seller_handle}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-[10px]">{e.listing_id && !e.orphan_dangling ? e.listing_id.slice(0, 8) + "…" : <span className="text-amber-600" title={e.orphan_dangling ? `Listing ${(e.listing_id || "").slice(0,8)}… existiert nicht mehr` : "Keinem Listing zugeordnet"}>Waise</span>}</td>
                <td className="px-3 py-2 text-xs text-[#666]">{fmtDate(e.created_at)}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={e.status} sched={e.scheduled_delete_at} reason={e.scheduled_delete_reason} />
                  {e.not_active && e.status !== "DELETED" && (
                    <span
                      data-testid={`worn-vault-inactive-${e.vault_id}`}
                      title={e.orphan_dangling
                        ? "Zugehöriges Listing existiert nicht mehr (dangling) – wird nach Ablauf der Orphan-Frist (3 Tage) automatisch gelöscht"
                        : "Keinem Listing zugeordnet – wird nach Ablauf der Orphan-Frist (3 Tage) automatisch gelöscht"}
                      className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700 text-[9px] font-semibold uppercase tracking-wide"
                    >
                      Verwaist
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{(e.linked_processed_paths || []).length}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex gap-1">
                    <button data-testid={`worn-vault-view-${e.vault_id}`} onClick={() => showOriginal(e.vault_id)}
                      disabled={e.status === "DELETED"}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5] disabled:opacity-40">
                      <Eye size={12} /> Original
                    </button>
                    <button data-testid={`worn-vault-detail-${e.vault_id}`} onClick={() => openDetail(e.vault_id)}
                      className="px-2 py-1 rounded border border-[#E5E5E5] text-xs hover:bg-[#F5F5F5]">
                      Details
                    </button>
                    <button data-testid={`worn-vault-delete-${e.vault_id}`} onClick={() => { setDeletingId(e.vault_id); setDeleteReason(""); }}
                      disabled={e.status === "DELETED"}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-xs text-red-600 hover:bg-red-50 disabled:opacity-40">
                      <Trash2 size={12} /> Löschen
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {deletingId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDeletingId(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-bold text-lg">Eintrag löschen</h3>
            <p className="text-sm text-[#666]">Kopplungs-Regel: Original + alle bearbeiteten Ableger werden synchron gelöscht.</p>
            <textarea data-testid="worn-vault-delete-reason" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)}
              rows={3} maxLength={200} placeholder="Grund (optional)"
              className="w-full px-3 py-2 rounded-lg border border-[#E5E5E5] text-sm" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeletingId(null)} className="px-4 py-2 rounded-lg border border-[#E5E5E5] text-sm">Abbrechen</button>
              <button data-testid="worn-vault-delete-confirm" onClick={() => doDelete(deletingId)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700">Endgültig löschen</button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={closePreview}>
          <button data-testid="worn-vault-preview-close" onClick={closePreview}
            className="absolute top-4 right-4 text-white p-2 rounded-full hover:bg-white/10">
            <X size={24} />
          </button>
          <img data-testid="worn-vault-preview-img" src={preview.url} alt="Original"
            className="max-w-full max-h-full rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg">Vault-Eintrag</h3>
              <button onClick={() => setDetail(null)} className="p-1"><X size={20} /></button>
            </div>
            <pre className="text-xs bg-[#FAFAFA] p-3 rounded-lg overflow-x-auto">{JSON.stringify(detail, null, 2)}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function GateHeader() {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="w-7 h-7 text-[#d97a63]" />
        <h1 className="text-xl font-bold">Worn-Originals-Vault</h1>
      </div>
      <div className="flex items-center gap-2 text-xs text-[#666]">
        <AlertTriangle size={14} className="text-amber-500" />
        Vertrauliche Original-Fotos. Zugriff wird protokolliert. Löschungen sind unwiderruflich.
      </div>
    </div>
  );
}

function ReadyHeader({ session, onLogout, onCleanup }) {
  return (
    <div className="mb-2">
      <div className="flex items-center gap-3 mb-1">
        <ShieldCheck className="w-6 h-6 text-[#d97a63]" />
        <h2 className="text-lg font-bold">Worn-Originals-Vault</h2>
      </div>
      <div className="flex items-center gap-3 text-xs">
        <span className="text-[#666]">Session gültig bis {fmtDate(session?.expires)}</span>
        <button data-testid="worn-vault-cleanup" onClick={onCleanup} className="text-[#d97a63] hover:underline">Cleanup ausführen</button>
        <button data-testid="worn-vault-logout" onClick={onLogout} className="ml-auto text-[#666] hover:underline">Session beenden</button>
      </div>
    </div>
  );
}

function StatusBadge({ status, sched, reason }) {
  const map = {
    ACTIVE: ["bg-emerald-50 text-emerald-700 border-emerald-200", "Aktiv"],
    SCHEDULED_DELETE: ["bg-amber-50 text-amber-700 border-amber-200", "Löschung geplant"],
    DELETED: ["bg-gray-100 text-gray-500 border-gray-200", "Gelöscht"],
  };
  const [cls, label] = map[status] || ["bg-gray-100 text-gray-600 border-gray-200", status];
  return (
    <div>
      <span className={`inline-block px-2 py-0.5 rounded border text-xs ${cls}`}>{label}</span>
      {status === "SCHEDULED_DELETE" && sched && (
        <div className="text-[10px] text-[#888] mt-0.5">bis {fmtDate(sched)} · {reason}</div>
      )}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}
